import React, { useState, useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import { Radio, Mic, Zap, ArrowRight, AlertTriangle, TrendingUp, Users, Activity, Target, X, ChevronUp, Shield, HeartHandshake, Cpu, Terminal, Brain, Ghost, Layers } from 'lucide-react';
import clsx from 'clsx';
import { ActionType } from '../types/game';
import { Tooltip } from './Tooltip';
import { CapabilityPanel } from './CapabilityPanel';

export const ACTION_DETAILS: Record<string, { citation: string, task: string, purpose: string, method: string, state: string }> = {
  Secure: {
    citation: "JP 3-13.3 OPSEC",
    task: "Protect Critical Info",
    purpose: "Prevent adversary collection",
    method: "Signature reduction",
    state: "Secured"
  },
  Broadcast: {
    citation: "AP 3-13.1 Public Affairs",
    task: "Inform Public",
    purpose: "Counter disinformation",
    method: "Press releases",
    state: "Informed"
  },
  Engage: {
    citation: "FM 3-24 Counterinsurgency",
    task: "Engage Leaders",
    purpose: "Build legitimacy",
    method: "Face-to-face meetings",
    state: "Aligned"
  },
  Listen: {
    citation: "Army FM 2-0 Intelligence",
    task: "Collect Intel",
    purpose: "Identify threats",
    method: "ISR assets",
    state: "Monitored"
  },
  Jam: {
    citation: "JP 3-51 Electronic Warfare",
    task: "Deny Spectrum",
    purpose: "Disrupt C2",
    method: "Electronic attack",
    state: "Jammed"
  },
  Assist: {
    citation: "FM 3-07 Stability Operations",
    task: "Provide Support",
    purpose: "Gain civil support",
    method: "Humanitarian aid",
    state: "Supported"
  },
  DeployTech: {
    citation: "JP 3-13.2 Space Operations",
    task: "Deploy Sensors",
    purpose: "Enhance collection",
    method: "Technical emplacement",
    state: "Sensored"
  },
  Hack: {
    citation: "FM 3-38 Cyber Operations",
    task: "Exploit Systems",
    purpose: "Degrade networks",
    method: "Cyber attack",
    state: "Compromised"
  },
  Influence: {
    citation: "FM 3-53 Psychological Operations",
    task: "Influence Audience",
    purpose: "Change behavior",
    method: "Psychological actions",
    state: "Persuaded"
  },
  Decoy: {
    citation: "JP 3-13.4 Military Deception",
    task: "Deceive Enemy",
    purpose: "Mislead decision makers",
    method: "Decoys and ruses",
    state: "Deceived"
  }
};

const ACTIONS: { id: ActionType, label: string, icon: any, color: string, unlockLevel: number }[] = [
  { id: 'Secure', label: 'Secure (OPSEC)', icon: Shield, color: 'text-blue-400', unlockLevel: 1 },
  { id: 'Broadcast', label: 'Broadcast (PAO)', icon: Radio, color: 'text-green-400', unlockLevel: 1 },
  { id: 'Listen', label: 'Listen (Intel)', icon: Activity, color: 'text-yellow-400', unlockLevel: 2 },
  { id: 'Jam', label: 'Jam (EW)', icon: Zap, color: 'text-red-400', unlockLevel: 2 },
  { id: 'Assist', label: 'Assist (CMO)', icon: HeartHandshake, color: 'text-teal-400', unlockLevel: 3 },
  { id: 'DeployTech', label: 'Deploy Tech', icon: Cpu, color: 'text-indigo-400', unlockLevel: 3 },
  { id: 'Hack', label: 'Hack (Cyber)', icon: Terminal, color: 'text-purple-400', unlockLevel: 4 },
  { id: 'Decoy', label: 'Decoy (Deception)', icon: Ghost, color: 'text-pink-400', unlockLevel: 4 },
  { id: 'Influence', label: 'Influence (PSYOP)', icon: Brain, color: 'text-orange-400', unlockLevel: 5 },
  { id: 'Engage', label: 'Engage (KLE)', icon: Users, color: 'text-cyan-400', unlockLevel: 5 },
  { id: 'Extend_ILOE', label: 'Extend ILOE', icon: Layers, color: 'text-white', unlockLevel: 1 },
  { id: 'Surge_Capability', label: 'Surge Capability', icon: TrendingUp, color: 'text-white', unlockLevel: 1 },
  { id: 'Trade_Capacity', label: 'Trade Capacity', icon: ArrowRight, color: 'text-white', unlockLevel: 1 }
];

export const ActionPanel: React.FC = () => {
  const { ap, performAction, selectedSectorId, sectors, turn, blockedAction, clearBlockedAction } = useGameStore();
  const [selectedAction, setSelectedAction] = useState<ActionType | null>(null);
  const [activeTab, setActiveTab] = useState<'actions' | 'capabilities'>('actions');

  const selectedSector = sectors.find(s => s.id === selectedSectorId);
  
  // Calculate current level based on turn (simplified progression)
  const currentLevel = Math.min(5, Math.ceil(turn / 2));

  const handleActionClick = (actionId: ActionType) => {
    if (ap <= 0) {
      // MIN-02: clear feedback when tapping actions with 0 AP
      setSelectedAction(null);
      return;
    }
    setSelectedAction(actionId);
  };

  const confirmAction = () => {
    if (selectedAction) {
      performAction(selectedAction);
      setSelectedAction(null);
    }
  };

  return (
    <div className="absolute right-4 top-20 bottom-20 w-80 bg-slate-900/90 border border-slate-700 rounded-lg flex flex-col shadow-2xl backdrop-blur-md z-30 pointer-events-auto">
      
      {/* Tabs */}
      <div className="flex border-b border-slate-700">
        <button
          onClick={() => setActiveTab('actions')}
          className={clsx(
            "flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-colors",
            activeTab === 'actions' ? "bg-slate-800 text-blue-400 border-b-2 border-blue-400" : "text-slate-500 hover:text-slate-300"
          )}
        >
          Actions
        </button>
        <button
          onClick={() => setActiveTab('capabilities')}
          className={clsx(
            "flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-colors",
            activeTab === 'capabilities' ? "bg-slate-800 text-purple-400 border-b-2 border-purple-400" : "text-slate-500 hover:text-slate-300"
          )}
        >
          Capabilities
        </button>
      </div>

      {activeTab === 'capabilities' ? (
        <CapabilityPanel />
      ) : (
        <>
          <div className="p-4 border-b border-slate-700 bg-slate-800/50">
            <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-400" />
              {selectedSector ? selectedSector.name : 'Select Sector'}
            </h2>
            {selectedSector && (
              <div className="text-xs text-slate-400 mt-1">
                Trust: <span className={selectedSector.trustScore < 4 ? 'text-red-400' : 'text-green-400'}>{selectedSector.trustScore}/10</span>
              </div>
            )}
            {/* MIN-02: Insufficient AP warning */}
            {ap <= 0 && (
              <div className="mt-2 px-3 py-1.5 bg-red-900/30 border border-red-700/50 rounded text-xs text-red-400 font-mono flex items-center gap-2">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                INSUFFICIENT AP — END TURN to restore
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-700 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
            {ACTIONS.filter(a => !['Extend_ILOE', 'Surge_Capability', 'Trade_Capacity'].includes(a.id)).map((action) => {
              const isLocked = action.unlockLevel > currentLevel;
              const details = ACTION_DETAILS[action.id];
              
              return (
                <div key={action.id} className="relative group">
                  <button
                    onClick={() => handleActionClick(action.id)}
                    disabled={isLocked || ap <= 0 || !selectedSectorId}
                    className={clsx(
                      "w-full flex items-center justify-between p-3 rounded border transition-all",
                      selectedAction === action.id
                        ? "bg-blue-900/40 border-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.3)]"
                        : isLocked
                        ? "bg-slate-900/50 border-slate-800 opacity-50 cursor-not-allowed"
                        : "bg-slate-800/50 border-slate-700 hover:bg-slate-700 hover:border-slate-600"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <action.icon className={clsx("w-5 h-5", isLocked ? "text-slate-600" : action.color)} />
                      <div className="text-left">
                        <div className={clsx("font-bold text-sm", isLocked ? "text-slate-500" : "text-slate-200")}>
                          {action.label}
                        </div>
                        {!isLocked && details && (
                          <div className="text-[10px] text-slate-500 font-mono">{details.citation}</div>
                        )}
                      </div>
                    </div>
                    {isLocked && (
                      <div className="text-xs text-slate-600 font-mono">Lvl {action.unlockLevel}</div>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Confirmation Panel */}
          {selectedAction && ACTION_DETAILS[selectedAction] && (
            <div className="p-4 bg-slate-800 border-t border-slate-700 animate-slideUp">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-bold text-white flex items-center gap-2">
                  <Activity className="w-4 h-4 text-blue-400" />
                  Confirm Action
                </h3>
                <button onClick={() => setSelectedAction(null)} className="text-slate-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-2 text-xs mb-4">
                <div className="grid grid-cols-[60px_1fr] gap-2">
                  <span className="text-slate-500 font-mono uppercase">Task</span>
                  <span className="text-slate-200">{ACTION_DETAILS[selectedAction].task}</span>
                </div>
                <div className="grid grid-cols-[60px_1fr] gap-2">
                  <span className="text-slate-500 font-mono uppercase">Purpose</span>
                  <span className="text-slate-200">{ACTION_DETAILS[selectedAction].purpose}</span>
                </div>
                <div className="grid grid-cols-[60px_1fr] gap-2">
                  <span className="text-slate-500 font-mono uppercase">Method</span>
                  <span className="text-slate-200">{ACTION_DETAILS[selectedAction].method}</span>
                </div>
                <div className="grid grid-cols-[60px_1fr] gap-2">
                  <span className="text-slate-500 font-mono uppercase">End State</span>
                  <span className="text-blue-300">{ACTION_DETAILS[selectedAction].state}</span>
                </div>
                <div className="grid grid-cols-[60px_1fr] gap-2 mt-2 pt-2 border-t border-slate-700">
                  <span className="text-slate-500 font-mono uppercase">Ref</span>
                  <span className="text-yellow-500/80 font-mono">{ACTION_DETAILS[selectedAction].citation}</span>
                </div>
              </div>

              <button
                onClick={confirmAction}
                className="w-full py-2 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-blue-500/25"
              >
                <Zap className="w-4 h-4" />
                EXECUTE (1 AP)
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
